import { useState, type ChangeEvent } from 'react';
import { Alert, AlertTitle, Box, Button, CircularProgress, Stack, TextField } from '@mui/material';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { useNotify, useRefresh, type RaRecord } from 'react-admin';
import { useFeature } from '../admin/featureFlags/FeatureGate';
import { dateTimeNoSeconds } from '../shared/dateTime';
import { toErrorMessage } from './preOrderApi';
import {
  FEATURE_PRE_ORDER_ARCHIVE,
  MIN_ARCHIVE_REASON_LENGTH,
  archivePreOrder,
  isArchived,
  restorePreOrder,
} from './preOrderArchiveApi';

/**
 * Ręczna archiwizacja prezlecenia (decyzja użytkownika 24.10.2026, A6:
 * prezlecenie nie wygasa samo — można je ręcznie zarchiwizować;
 * przełącznik `preOrderArchive`, S-25.10-01). Reguły: API
 * `pre-order/pre-order-archive.ts`.
 */

/** Powód blokady zmian przy zarchiwizowanym — do `PreOrderActions`. */
export const ARCHIVED_LOCK_REASON =
  'Prezlecenie jest zarchiwizowane — przywróć je („Przywróć”), żeby je zmienić albo zaakceptować.';

/** Kto, kiedy i dlaczego zarchiwizował — widzi każdy, kto ogląda kartę. */
export const PreOrderArchivedBanner = ({ record }: { record: RaRecord }) => {
  const on = useFeature(FEATURE_PRE_ORDER_ARCHIVE);
  if (!on || !isArchived(record)) return null;
  const by = typeof record.archivedBy === 'string' && record.archivedBy ? ` przez ${record.archivedBy}` : '';
  return (
    <Alert severity="warning" data-testid="pre-order-archived">
      <AlertTitle>
        Zarchiwizowane {dateTimeNoSeconds(record.archivedAt as string)}
        {by}
      </AlertTitle>
      Powód: {String(record.archivedReason ?? '—')}. Prezlecenie nie pojawia się na listach ani w kolejkach — żeby je
      zmienić albo zaakceptować, trzeba je przywrócić.
    </Alert>
  );
};

/**
 * „Archiwizuj” / „Przywróć” z powodem (przyjęcie zleceń: CS, Administracja,
 * ich liderzy, administratorzy — trasa API sprawdza uprawnienie i dział).
 * Zaakceptowanego nie archiwizujemy — przycisku nie ma, a API i tak odmawia.
 */
export const PreOrderArchiveActions = ({
  record,
  lockedReason = null,
}: {
  record: RaRecord;
  /** Rekord u drugiego działu — tylko on archiwizuje (API odmawia 409). */
  lockedReason?: string | null;
}) => {
  const on = useFeature(FEATURE_PRE_ORDER_ARCHIVE);
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  if (!on) return null;
  const archived = isArchived(record);
  if (!archived && record.acceptedAt != null) return null;

  const enough = reason.trim().length >= MIN_ARCHIVE_REASON_LENGTH;
  const submit = async () => {
    setBusy(true);
    try {
      const r = archived ? await restorePreOrder(record.id, reason) : await archivePreOrder(record.id, reason);
      notify(
        `${archived ? 'Prezlecenie przywrócone' : 'Prezlecenie zarchiwizowane'}${r.bankNote ? `. ${r.bankNote}` : ''}`,
        { type: 'success' },
      );
      setOpen(false);
      setReason('');
      refresh();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box data-testid="pre-order-archive-actions">
      {!open ? (
        <Button
          size="small"
          variant="outlined"
          color={archived ? 'primary' : 'warning'}
          startIcon={archived ? <UnarchiveIcon /> : <ArchiveIcon />}
          disabled={busy || lockedReason !== null}
          onClick={() => setOpen(true)}
          data-testid={archived ? 'pre-order-restore' : 'pre-order-archive'}
        >
          {archived ? 'Przywróć' : 'Archiwizuj'}
        </Button>
      ) : (
        <Stack spacing={1}>
          <TextField
            label={archived ? 'Powód przywrócenia (wymagany)' : 'Powód archiwizacji (wymagany)'}
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={reason}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
            helperText={`Co najmniej ${MIN_ARCHIVE_REASON_LENGTH} znaków — zostaje w historii prezlecenia.`}
            slotProps={{ htmlInput: { 'data-testid': 'pre-order-archive-reason' } }}
          />
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Button
              size="small"
              variant="contained"
              color={archived ? 'primary' : 'warning'}
              disabled={busy || !enough}
              onClick={() => void submit()}
              data-testid="pre-order-archive-confirm"
            >
              {archived ? 'Przywróć prezlecenie' : 'Zarchiwizuj'}
            </Button>
            <Button size="small" disabled={busy} onClick={() => setOpen(false)}>
              Anuluj
            </Button>
            {busy ? <CircularProgress size={18} /> : null}
          </Stack>
        </Stack>
      )}
    </Box>
  );
};
